import type { BreadcrumbItem, BreadcrumbsProps } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { CheckboxField, ColorField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

function newId(): string {
  return `b-${Math.random().toString(36).slice(2, 8)}`;
}

export function BreadcrumbsInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as BreadcrumbsProps;
  const items = p.items ?? [];

  const setItems = (next: BreadcrumbItem[]) => update(widget.id, { items: next });
  const addItem = () => setItems([...items, { id: newId(), label: "New", href: "/" }]);
  const updateItem = (id: string, patch: Partial<BreadcrumbItem>) =>
    setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const removeItem = (id: string) => setItems(items.filter((it) => it.id !== id));
  const moveItem = (id: string, dir: "up" | "down") => {
    const idx = items.findIndex((it) => it.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setItems(next);
  };

  return (
    <>
      <TextField
        label="Separator"
        value={p.separator ?? "/"}
        onChange={(v) => update(widget.id, { separator: v })}
      />
      <ColorField
        label="Text colour"
        value={p.textColor ?? "#6b7280"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />
      <ColorField
        label="Link colour"
        value={p.linkColor ?? "#111827"}
        onChange={(v) => update(widget.id, { linkColor: v })}
      />
      <CheckboxField
        label="Emit schema.org BreadcrumbList JSON-LD"
        value={p.emitSchemaOrg ?? true}
        onChange={(v) => update(widget.id, { emitSchemaOrg: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Items ({items.length})
      </div>

      {items.map((it, i) => (
        <div
          key={it.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "up")}
              disabled={i === 0}
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "down")}
              disabled={i === items.length - 1}
              style={{
                padding: "2px 6px",
                opacity: i === items.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeItem(it.id)}
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>
          <TextField
            label={`Label ${i + 1}`}
            value={it.label}
            onChange={(v) => updateItem(it.id, { label: v })}
          />
          <TextField
            label="URL (optional on last)"
            value={it.href ?? ""}
            onChange={(v) => updateItem(it.id, { href: v || undefined })}
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addItem}
          style={{ width: "100%" }}
        >
          + Add crumb
        </button>
      </div>
    </>
  );
}
